import React from 'react';
import styled from 'styled-components';
import {
    buildImageSource,
    buildImageSourceSet,
    getBaseImageUrl,
} from '../../utils/buildImageSource';
import { getMdsUrl, type MDSAliases } from '../../utils/mds/getMdsUrl';
import { Image } from '../media/Image';
import { Img } from '../media/Img';
import type { ContentUI } from './types';

const VideoTag = styled.video.withConfig({ displayName: 'VideoTag' }).attrs({
    playsInline: true,
    muted: true,
    preload: 'auto',
    autoPlay: false,
})`
    object-fit: cover;
    object-position: center;
`;

interface MediaPreviewProps {
    namespace?: string;
    media: ContentUI;
    lazyLoad?: boolean;
    imageAlias?: MDSAliases;
    alt?: string;
}

function resolveImageSrc(
    images: string | Inspire.Image[] | undefined,
    namespace: string,
    imageAlias?: MDSAliases,
): string | undefined {
    if (!images) {
        return undefined;
    }

    if (imageAlias) {
        const baseUrl = getBaseImageUrl(images);

        return baseUrl ? getMdsUrl(baseUrl, imageAlias) : undefined;
    }

    return buildImageSource(images, namespace);
}

export const MediaPreview = (props: MediaPreviewProps) => {
    const { media, namespace = '', lazyLoad = true, imageAlias, alt } = props;

    const ImgComponent = lazyLoad ? Image : Img;

    if (media.type === 'image') {
        return (
            <ImgComponent
                src={resolveImageSrc(media.media, namespace, imageAlias)}
                srcSet={imageAlias ? undefined : buildImageSourceSet(media.media, namespace)}
                alt={alt}
            />
        );
    }

    const [firstMedia] = media.media;

    if (media.type === 'video') {
        if (firstMedia.previewFormats) {
            return (
                <ImgComponent
                    src={resolveImageSrc(firstMedia.previewFormats, namespace, imageAlias)}
                    srcSet={
                        imageAlias
                            ? undefined
                            : buildImageSourceSet(firstMedia.previewFormats, namespace)
                    }
                    alt={alt}
                />
            );
        }
        if (firstMedia.firstFrameUrl) {
            const src = imageAlias
                ? getMdsUrl(firstMedia.firstFrameUrl, imageAlias)
                : firstMedia.firstFrameUrl;

            return <ImgComponent src={src} alt={alt} />;
        }

        return (
            <VideoTag>
                <source src={firstMedia.url} />
            </VideoTag>
        );
    }

    return null;
};
